/**
 * CMS 富文本清洗。后台编辑器产出的 HTML 有两类问题，都在渲染前修掉：
 *
 * 1) 附件锚点把 URL 写在 src 上：
 *      <a src="https://.../xxx.pdf" title="xxx.pdf">xxx.pdf</a>
 *    <a> 上没有 href 就不是超链接，浏览器不给它描点、不可聚焦、点击无反应；
 *    内联的 color:#0066cc 让它看起来像链接，所以很难一眼看出问题。
 *
 * 2) 正文里的站内链接硬编码成 http://，而站点只提供 HTTPS：
 *      <a href="http://www.zjmegroup.cn/file/at/file/xxx.xls">附件.xls</a>
 *    实测 www.zjmegroup.cn 的 80 端口不响应，同样的路径走 443 才是 200。
 *    HTTPS 页面上点这种链接，浏览器会把下载拦掉（点一下什么都不会发生），
 *    PDF 之类的导航则得到一个空白页。
 *
 * 只升级“我们自己域名”的链接：外站的 http 链接无法确认其 https 是否可用，不做猜测。
 */

// CMS 生成的站内地址用的域名（后台站点配置）。不能只靠 location.host 判断：
// 测试环境/本地预览的页面域名不是它，但正文里的链接仍然是它，那些环境同样要能点开。
const CMS_SITE_HOST = "www.zjmegroup.cn";

// 匹配开标签里的属性串
const OPEN_TAG = /<a\b([^>]*)>/gi;
// 单独取某个属性的值，支持双引号、单引号、无引号三种写法
const attrValue = (attrs, name) => {
  const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(attrs);
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? null;
};

// 只认“看起来像地址”的值，避免把编辑器里其他用途的 src 误改成跳转链接
const looksLikeUrl = (v) => /^(https?:)?\/\/|^\//i.test(v.trim());

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// 把指定域名下 href/src 的 http:// 升级为 https://
function upgradeHost(html, host) {
  if (!host) return html;
  const re = new RegExp(
    `(\\b(?:href|src)\\s*=\\s*("|'))http://${escapeRe(host)}(?=["'/?#\\s>])`,
    "gi"
  );
  return html.replace(re, (_m, attr) => `${attr}https://${host}`);
}

function upgradeOwnHosts(html, pageHost) {
  let out = upgradeHost(html, CMS_SITE_HOST);
  // 页面域名与 CMS 域名不同时（本地/测试环境）额外匹配一次
  if (pageHost && pageHost !== CMS_SITE_HOST) out = upgradeHost(out, pageHost);
  return out;
}

/**
 * @param {string} html        CMS 富文本
 * @param {string} [pageHost]  当前页面域名，默认取 location.host；显式传入便于测试
 */
export function normalizeRichText(html, pageHost) {
  if (typeof html !== "string" || html === "") return html;

  const host = pageHost ?? (typeof location !== "undefined" ? location.host : "");
  let out = html;

  if (out.indexOf("<a") !== -1) {
    out = out.replace(OPEN_TAG, (tag, attrs) => {
      // 已经有 href 的链接保持原样
      if (attrValue(attrs, "href")) return tag;

      const src = attrValue(attrs, "src");
      if (!src || !looksLikeUrl(src)) return tag;

      // 去掉已经转成 href 的 src，保留 style / title 等原有属性
      const kept = attrs.replace(/\s*\bsrc\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i, "").trim();

      return `<a href="${src}" target="_blank" rel="noopener noreferrer"${kept ? " " + kept : ""}>`;
    });
  }

  return upgradeOwnHosts(out, host);
}

export default normalizeRichText;
